import { ApplicationRef, Injectable, NgZone } from "@angular/core";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { concat, interval } from "rxjs";
import { filter, first } from "rxjs/operators";
import {
	SW_SESSION_UPGRADE_HANDLED,
	SW_SESSION_UPGRADE_PENDING,
	SW_SESSION_UPGRADE_STORAGE_KEY,
} from "src/session.config";
import {
	UpdateSnackbarComponent,
	UpdateSnackbarData,
} from "../../shared/components/cbta-snackbar/update-snackbar.component";
import { AVATAR_CACHE_NAME } from "../../shared/utils/avatar-cache.utils";
import { fetchLatestAppVersionFromNetwork, getCurrentAppVersion } from "../app-version";

export type ManualCheckResult = "update-ready" | "up-to-date" | "disabled" | "error";

@Injectable({ providedIn: "root" })
export class AppUpdateService {
	// how often to check for updates while the app stays open
	private readonly checkIntervalMs = 10 * 60 * 1000; // 10 minutes
	private readonly sessionUpgradePollAttempts = 10;
	private readonly sessionUpgradePollIntervalMs = 300;
	private updateSnackbarRef: MatSnackBarRef<UpdateSnackbarComponent> | null = null;
	private hasVersionReady = false;

	constructor(
		private readonly appRef: ApplicationRef,
		private readonly swUpdate: SwUpdate,
		private readonly snackBar: MatSnackBar,
		private readonly zone: NgZone,
	) {
		if (!this.swUpdate.isEnabled) return;

		// 1) kick off checks when app is stable + every X hours
		const appIsStable$ = this.appRef.isStable.pipe(first((stable) => stable));
		const periodic$ = interval(this.checkIntervalMs);
		concat(appIsStable$, periodic$).subscribe(() => {
			this.zone.run(() => this.safeCheckForUpdate());
		});

		// 2) when a new version is ready (downloaded), prompt the user
		this.swUpdate.versionUpdates
			.pipe(filter((e): e is VersionReadyEvent => e.type === "VERSION_READY"))
			.subscribe(() => {
				this.hasVersionReady = true;
				this.promptToReload();
			});

		// 3) fatal SW error — suggest a reload
		this.swUpdate.unrecoverable.subscribe((evt) => {
			console.error("Service worker unrecoverable:", evt.reason);
			void this.applyPendingUpdateAndReload().catch(() => location.reload());
		});
	}

	private async safeCheckForUpdate(): Promise<void> {
		try {
			const result = await this.swUpdate.checkForUpdate();
			if (result) {
				this.hasVersionReady = true;
			}
			console.log("Check for update result:", result);
		} catch (error) {
			console.error("Error checking for updates:", error);
		}
	}

	async hasWaitingWorker(): Promise<boolean> {
		if (!("serviceWorker" in navigator)) {
			return false;
		}
		const registration = await navigator.serviceWorker.getRegistration();
		return !!registration?.waiting;
	}

	async clearAngularCaches(): Promise<void> {
		if (!("caches" in window)) {
			return;
		}
		const keys = await caches.keys();
		await Promise.all(keys.filter((key) => key !== AVATAR_CACHE_NAME).map((key) => caches.delete(key)));
	}

	async unregisterAllAndClearCaches(): Promise<void> {
		if ("serviceWorker" in navigator) {
			const registrations = await navigator.serviceWorker.getRegistrations();
			await Promise.all(registrations.map((registration) => registration.unregister()));
		}
		await this.clearAngularCaches();
	}

	async unregisterAndClearCaches(): Promise<void> {
		await this.unregisterAllAndClearCaches();
	}

	async applyPendingUpdateAndReload(): Promise<void> {
		try {
			const activated = await this.swUpdate.activateUpdate();
			this.hasVersionReady = false;
			// The new version is already prefetched when activateUpdate returns true.
			// Deleting caches in that case would leave the controlling worker with no
			// shell, so the reload must download index.html. A failed download rejects
			// the navigation and Safari shows the 504 page.
			// However, if activateUpdate returns false while the live network shell has
			// a newer version, the SW did not stage the new manifest and would keep
			// serving the stale cached shell unless unregistered.
			if (!activated && this.isBrowserOnline() && (await this.hasAppVersionMismatch())) {
				await this.unregisterAllAndClearCaches();
			}
		} catch (error) {
			console.warn("activateUpdate failed, unregistering service worker:", error);
			this.hasVersionReady = false;
			if (this.isBrowserOnline()) {
				await this.unregisterAllAndClearCaches();
				await this.clearAngularCaches();
			}
		}
		this.reloadApplication();
	}

	private isBrowserOnline(): boolean {
		return typeof navigator === "undefined" || navigator.onLine !== false;
	}

	private reloadApplication(): void {
		location.reload();
	}

	private promptToReload(): void {
		if (this.updateSnackbarRef) {
			return;
		}

		const data: UpdateSnackbarData = {
			onReload: () => this.applyPendingUpdateAndReload(),
		};

		this.updateSnackbarRef = this.snackBar.openFromComponent(UpdateSnackbarComponent, {
			data,
			duration: 0,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["update-snackbar"],
		});

		this.updateSnackbarRef.afterDismissed().subscribe(() => {
			this.updateSnackbarRef = null;
		});
	}

	private showInfoSnackBar(message: string): void {
		this.snackBar.open(message, "OK", {
			duration: 4000,
			horizontalPosition: "center",
			verticalPosition: "top",
		});
	}

	private readSessionUpgradeState(): string | null {
		try {
			return sessionStorage.getItem(SW_SESSION_UPGRADE_STORAGE_KEY);
		} catch {
			return null;
		}
	}

	private markSessionUpgradeHandled(): void {
		try {
			sessionStorage.setItem(SW_SESSION_UPGRADE_STORAGE_KEY, SW_SESSION_UPGRADE_HANDLED);
		} catch {
			// ignore
		}
	}

	private sleep(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	private async pollForWaitingWorker(): Promise<boolean> {
		for (let attempt = 0; attempt < this.sessionUpgradePollAttempts; attempt++) {
			if (await this.hasWaitingWorker()) {
				return true;
			}
			await this.sleep(this.sessionUpgradePollIntervalMs);
		}
		return false;
	}

	private async hasAppVersionMismatch(): Promise<boolean> {
		if (!this.isBrowserOnline()) {
			return false;
		}
		const clientVersion = getCurrentAppVersion();
		if (!clientVersion) {
			return false;
		}
		const serverVersion = await fetchLatestAppVersionFromNetwork();
		if (!serverVersion) {
			return false;
		}
		return serverVersion !== clientVersion;
	}

	private async forceRefreshFromNetwork(): Promise<void> {
		await this.unregisterAllAndClearCaches();
		this.reloadApplication();
	}

	private async forceSessionUpgradeRefreshIfNeeded(): Promise<boolean> {
		const state = this.readSessionUpgradeState();
		if (state === SW_SESSION_UPGRADE_HANDLED) {
			return false;
		}
		if (state === SW_SESSION_UPGRADE_PENDING) {
			this.markSessionUpgradeHandled();
			return false;
		}

		try {
			sessionStorage.setItem(SW_SESSION_UPGRADE_STORAGE_KEY, SW_SESSION_UPGRADE_PENDING);
		} catch {
			// ignore
		}
		await this.forceRefreshFromNetwork();
		return true;
	}

	/** After BFF session 401 redirect: check and reload immediately if a new bundle is ready. */
	async applyUpdateIfReady(): Promise<boolean> {
		if (!this.swUpdate.isEnabled) {
			if (await this.hasAppVersionMismatch()) {
				await this.forceRefreshFromNetwork();
				return true;
			}
			return false;
		}

		try {
			const downloaded = await this.swUpdate.checkForUpdate();
			if (downloaded) {
				this.hasVersionReady = true;
			}
			const waiting = await this.hasWaitingWorker();
			const versionMismatch = await this.hasAppVersionMismatch();
			if (downloaded || waiting || this.hasVersionReady || versionMismatch) {
				await this.applyPendingUpdateAndReload();
				return true;
			}
		} catch (error) {
			console.error("Error applying session-upgrade update:", error);
			if (await this.hasAppVersionMismatch()) {
				await this.forceRefreshFromNetwork();
				return true;
			}
		}

		return false;
	}

	/** Logout / session-expiry path: poll, activate, compare versions, then force one network refresh. */
	async applySessionUpgradeUpdate(): Promise<boolean> {
		const upgradeState = this.readSessionUpgradeState();
		if (upgradeState === SW_SESSION_UPGRADE_HANDLED) {
			return false;
		}
		if (upgradeState === SW_SESSION_UPGRADE_PENDING) {
			this.markSessionUpgradeHandled();
		}

		if (!this.swUpdate.isEnabled) {
			return this.forceSessionUpgradeRefreshIfNeeded();
		}

		try {
			const downloaded = await this.swUpdate.checkForUpdate();
			if (downloaded || this.hasVersionReady || (await this.pollForWaitingWorker())) {
				await this.applyPendingUpdateAndReload();
				return true;
			}

			if (await this.hasAppVersionMismatch()) {
				await this.forceRefreshFromNetwork();
				return true;
			}
		} catch (error) {
			console.error("Error applying session-upgrade update:", error);
		}

		return this.forceSessionUpgradeRefreshIfNeeded();
	}

	/** Call from login page or header menu to check for updates manually. */
	public async manualCheck(): Promise<ManualCheckResult> {
		console.log("Manual check for updates...");

		if (!this.swUpdate.isEnabled) {
			if (await this.hasAppVersionMismatch()) {
				this.promptToReload();
				return "update-ready";
			}
			this.showInfoSnackBar("App updates are not available in this environment.");
			return "disabled";
		}

		try {
			const downloaded = await this.swUpdate.checkForUpdate();
			if (downloaded) {
				this.hasVersionReady = true;
			}
			const waiting = await this.hasWaitingWorker();
			const versionMismatch = await this.hasAppVersionMismatch();
			console.log("Manual check for update result:", {
				downloaded,
				waiting,
				versionReady: this.hasVersionReady,
				versionMismatch,
			});

			if (downloaded || waiting || this.hasVersionReady || versionMismatch) {
				this.promptToReload();
				return "update-ready";
			}

			this.showInfoSnackBar("No newer version to download.");
			return "up-to-date";
		} catch (error) {
			console.error("Error checking for manual updates:", error);
			if (await this.hasAppVersionMismatch()) {
				this.promptToReload();
				return "update-ready";
			}
			this.showInfoSnackBar("Error checking for updates.");
			return "error";
		}
	}
}
